<template>
	<div
		class="workModeTiles"
		role="radiogroup"
	>
		<button
			:aria-checked="modelValue"
			:class="['workModeTiles__tile', { 'workModeTiles__tile--active': modelValue }]"
			@click="selectMode(true)"
			role="radio"
			type="button"
		>
			<span class="workModeTiles__mock">
				<span class="workModeTiles__mockHeader">
					<span class="workModeTiles__mockHeaderTitle" />
					<span class="workModeTiles__mockHeaderAction" />
				</span>
				<span class="workModeTiles__mockBody">
					<span class="workModeTiles__mockMenu">
						<span class="workModeTiles__mockMenuItem workModeTiles__mockMenuItem--active" />
						<span class="workModeTiles__mockMenuItem" />
						<span class="workModeTiles__mockMenuItem" />
						<span class="workModeTiles__mockMenuItem" />
					</span>
					<span class="workModeTiles__mockMain">
						<span class="workModeTiles__mockTabRow">
							<span class="workModeTiles__mockTab workModeTiles__mockTab--active" />
							<span class="workModeTiles__mockTab" />
							<span class="workModeTiles__mockTab" />
						</span>
						<span class="workModeTiles__mockPane">
							<span class="workModeTiles__mockHeading" />
							<span class="workModeTiles__mockRow" />
							<span class="workModeTiles__mockRow" />
							<span class="workModeTiles__mockRow workModeTiles__mockRow--short" />
						</span>
					</span>
				</span>
			</span>
			<span class="workModeTiles__tileTitle">{{ $t('setupWizard_work_mode_tabs_title') }}</span>
			<span class="workModeTiles__tileDescription">{{ $t('setupWizard_work_mode_tabs_description') }}</span>
		</button>
		<button
			:aria-checked="!modelValue"
			:class="['workModeTiles__tile', { 'workModeTiles__tile--active': !modelValue }]"
			@click="selectMode(false)"
			role="radio"
			type="button"
		>
			<span class="workModeTiles__mock">
				<span class="workModeTiles__mockHeader">
					<span class="workModeTiles__mockHeaderTitle" />
					<span class="workModeTiles__mockHeaderAction" />
				</span>
				<span class="workModeTiles__mockBody">
					<span class="workModeTiles__mockMenu">
						<span class="workModeTiles__mockMenuItem workModeTiles__mockMenuItem--active" />
						<span class="workModeTiles__mockMenuItem" />
						<span class="workModeTiles__mockMenuItem" />
						<span class="workModeTiles__mockMenuItem" />
					</span>
					<span class="workModeTiles__mockMain workModeTiles__mockMain--split">
						<span class="workModeTiles__mockPane workModeTiles__mockPane--list">
							<span class="workModeTiles__mockRow workModeTiles__mockRow--selected" />
							<span class="workModeTiles__mockRow" />
							<span class="workModeTiles__mockRow" />
							<span class="workModeTiles__mockRow workModeTiles__mockRow--short" />
						</span>
						<span class="workModeTiles__mockPane">
							<span class="workModeTiles__mockHeading" />
							<span class="workModeTiles__mockRow" />
							<span class="workModeTiles__mockRow" />
							<span class="workModeTiles__mockRow workModeTiles__mockRow--short" />
						</span>
					</span>
				</span>
			</span>
			<span class="workModeTiles__tileTitle">{{ $t('setupWizard_work_mode_focus_title') }}</span>
			<span class="workModeTiles__tileDescription">{{ $t('setupWizard_work_mode_focus_description') }}</span>
		</button>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
	emits: ['update:modelValue'],
	name: 'WorkModeTileSelect',
	props: {
		modelValue: {
			required: true,
			type: Boolean,
		},
	},
	setup(props, { emit }) {
		function selectMode(workspaceTabsEnabled: boolean): void {
			if (workspaceTabsEnabled === props.modelValue) {
				return;
			}
			emit('update:modelValue', workspaceTabsEnabled);
		}

		return { selectMode };
	},
});
</script>

<style lang="scss" scoped>
.workModeTiles {
	display: grid;
	gap: 0.75rem;
	grid-template-columns: repeat(2, minmax(0, 1fr));

	@media (max-width: 480px) {
		grid-template-columns: minmax(0, 1fr);
	}
}

.workModeTiles__tile {
	align-items: stretch;
	background: var(--p-surface-0);
	border: 2px solid var(--p-surface-200);
	border-radius: 0.5rem;
	color: var(--p-text-color);
	cursor: pointer;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	padding: 0.875rem;
	text-align: left;
	transition:
		background-color 150ms ease,
		border-color 150ms ease;

	&:hover:not(.workModeTiles__tile--active) {
		background: var(--hover-light);
	}
}

.workModeTiles__tile--active {
	background: color-mix(in srgb, var(--p-primary-500) 8%, transparent);
	border-color: var(--p-primary-500);
}

.workModeTiles__tileTitle {
	font-size: var(--text-sm-plus);
	font-weight: 600;
}

.workModeTiles__tileDescription {
	color: var(--p-text-muted-color);
	font-size: var(--text-xs-plus);
}

.workModeTiles__mock {
	background: var(--p-surface-100);
	border: 1px solid var(--p-surface-200);
	border-radius: 0.375rem;
	display: flex;
	flex-direction: column;
	gap: 3px;
	height: 88px;
	padding: 6px;
}

.workModeTiles__mockTabRow {
	display: flex;
	gap: 3px;
}

.workModeTiles__mockTab {
	background: var(--p-surface-300);
	border-radius: 2px 2px 0 0;
	height: 8px;
	width: 22px;
}

.workModeTiles__mockTab--active {
	background: var(--p-primary-500);
}

.workModeTiles__mockBody {
	display: flex;
	flex: 1;
	gap: 3px;
	min-height: 0;
}

.workModeTiles__mockPane {
	background: var(--p-surface-0);
	border: 1px solid var(--p-surface-300);
	border-radius: 2px;
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 4px;
	min-height: 0;
	overflow: hidden;
	padding: 5px;
}

.workModeTiles__mockPane--list {
	flex: 0 0 38%;
}

.workModeTiles__mockMain {
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 3px;
	min-height: 0;
	min-width: 0;
}

.workModeTiles__mockMain--split {
	flex-direction: row;
}

.workModeTiles__mockHeader {
	align-items: center;
	background: var(--p-surface-0);
	border: 1px solid var(--p-surface-300);
	border-radius: 2px;
	display: flex;
	flex-shrink: 0;
	height: 10px;
	justify-content: space-between;
	padding: 0 3px;
}

.workModeTiles__mockHeaderTitle {
	background: var(--p-surface-400);
	border-radius: 1px;
	height: 3px;
	width: 30%;
}

.workModeTiles__mockHeaderAction {
	background: var(--p-surface-400);
	border-radius: 50%;
	height: 4px;
	width: 4px;
}

.workModeTiles__mockHeading {
	background: var(--p-surface-400);
	border-radius: 2px;
	flex-shrink: 0;
	height: 5px;
	width: 55%;
}

.workModeTiles__mockRow {
	background: var(--p-surface-200);
	border-radius: 2px;
	flex-shrink: 0;
	height: 4px;
	width: 100%;
}

.workModeTiles__mockRow--selected {
	background: color-mix(in srgb, var(--p-primary-500) 45%, transparent);
}

.workModeTiles__mockRow--short {
	width: 62%;
}

.workModeTiles__mockMenu {
	background: var(--p-surface-200);
	border-radius: 2px;
	display: flex;
	flex: 0 0 11%;
	flex-direction: column;
	gap: 3px;
	padding: 4px 3px;
}

.workModeTiles__mockMenuItem {
	background: var(--p-surface-400);
	border-radius: 1px;
	flex-shrink: 0;
	height: 3px;
	width: 100%;
}

.workModeTiles__mockMenuItem--active {
	background: var(--p-primary-500);
}
</style>

<style lang="scss">
.body--dark .workModeTiles__tile {
	background: var(--p-surface-800);
	border-color: var(--p-surface-700);
}

.body--dark .workModeTiles__tile:hover:not(.workModeTiles__tile--active) {
	background: var(--hover-dark);
}

.body--dark .workModeTiles__tile--active {
	background: color-mix(in srgb, var(--p-primary-400) 12%, transparent);
	border-color: var(--p-primary-400);
}

.body--dark .workModeTiles__mock {
	background: var(--p-surface-900);
	border-color: var(--p-surface-700);
}

.body--dark .workModeTiles__mockTab {
	background: var(--p-surface-600);
}

.body--dark .workModeTiles__mockTab--active {
	background: var(--p-primary-400);
}

.body--dark .workModeTiles__mockPane {
	background: var(--p-surface-800);
	border-color: var(--p-surface-600);
}

.body--dark .workModeTiles__mockHeader {
	background: var(--p-surface-800);
	border-color: var(--p-surface-600);
}

.body--dark .workModeTiles__mockHeaderTitle {
	background: var(--p-surface-500);
}

.body--dark .workModeTiles__mockHeaderAction {
	background: var(--p-surface-500);
}

.body--dark .workModeTiles__mockHeading {
	background: var(--p-surface-500);
}

.body--dark .workModeTiles__mockRow {
	background: var(--p-surface-700);
}

.body--dark .workModeTiles__mockRow--selected {
	background: color-mix(in srgb, var(--p-primary-400) 45%, transparent);
}

.body--dark .workModeTiles__mockMenu {
	background: var(--p-surface-700);
}

.body--dark .workModeTiles__mockMenuItem {
	background: var(--p-surface-500);
}
</style>
